/*
 * "Add to Home Screen" helpers.
 *
 * iOS only delivers web push to a site that has been added to the home screen, and it has no install API:
 * the user has to use Safari's Share menu. Chromium browsers expose `beforeinstallprompt` instead.
 */

const WORKER_URL = '/workers/pwa-sw.js'
const WORKER_SCOPE = '/workers/pwa/'

let deferredPrompt = null

if ( typeof window !== 'undefined' ) {
    // Keep the event so install can be triggered from a click later
    window.addEventListener( 'beforeinstallprompt', event => {
        event.preventDefault()
        deferredPrompt = event
    } )

    window.addEventListener( 'appinstalled', () => { deferredPrompt = null } )
}

export const isIOS = () =>
    typeof navigator !== 'undefined' && (
        /iphone|ipad|ipod/i.test( navigator.userAgent ) ||
        ( navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 )   // iPadOS reports as a Mac
    )

export const isStandalone = () =>
    typeof window !== 'undefined' && (
        window.matchMedia?.( '(display-mode: standalone)' ).matches ||
        window.navigator.standalone === true                                    // iOS Safari
    )

// True when notifications cannot work until the app is added to the home screen
export const needsInstallForPush = () => isIOS() && !isStandalone()

export const registerPwaWorker = async () => {
    if ( typeof navigator === 'undefined' || !( 'serviceWorker' in navigator ) ) return null

    try {
        return await navigator.serviceWorker.register( WORKER_URL, {
            scope: WORKER_SCOPE,
            updateViaCache: 'none'
        } )
    } catch ( err ) {
        console.error( err )
        return null
    }
}

/**
 * Starts the native install prompt where the browser supports it (Chromium).
 * Returns 'accepted' | 'dismissed', or 'unavailable' when the browser gives no prompt (iOS: show the manual steps).
 */
export const promptAddToHomeScreen = async () => {
    if ( !deferredPrompt ) return 'unavailable'

    const prompt = deferredPrompt
    deferredPrompt = null

    await prompt.prompt()
    const { outcome } = await prompt.userChoice
    return outcome
}
